import { useRef, type ReactNode } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { cn } from '@/lib/cn';

const reduce = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * HUD 진입 연출 — 자식 중 [data-hud] 요소를 stagger로 펼친다("콘솔 부팅" 느낌).
 * useGSAP 스코프 자동 cleanup. reduced-motion 시 즉시 표시.
 */
export function HudReveal({ children, className, stagger = 0.04 }: { children: ReactNode; className?: string; stagger?: number }) {
  const root = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      if (reduce()) return;
      const els = root.current?.querySelectorAll('[data-hud]');
      if (els && els.length) {
        gsap.from(els, { opacity: 0, y: 6, duration: 0.4, ease: 'power2.out', stagger });
      }
    },
    { scope: root },
  );
  return (
    <div ref={root} className={className}>
      {children}
    </div>
  );
}

/**
 * 숫자 카운트업 — 값 변경 시 현재→목표로 롤. tabular-nums 권장.
 * 키보드 핫패스(빠른 갱신)에선 reduce 시 즉시.
 */
export function NumberTicker({
  value,
  className,
  duration = 0.6,
  format,
}: {
  value: number;
  className?: string;
  duration?: number;
  format?: (n: number) => string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef(value);
  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const fmt = format ?? ((n: number) => String(Math.round(n)));
      if (reduce()) {
        el.textContent = fmt(value);
        prev.current = value;
        return;
      }
      const obj = { v: prev.current };
      gsap.to(obj, {
        v: value,
        duration,
        ease: 'power1.out',
        onUpdate: () => {
          el.textContent = fmt(obj.v);
        },
        onComplete: () => {
          prev.current = value;
        },
      });
    },
    { dependencies: [value], scope: ref },
  );
  return <span ref={ref} className={cn('tabular-nums', className)}>{format ? format(value) : Math.round(value)}</span>;
}
